import type { ReactNode } from "react";
import { AlertCircle, Inbox } from "lucide-react";
import { formatPlayerNo } from "@kickwin/shared";
import { cn } from "@/lib/utils";
import { Button as UiButton } from "@/components/ui/button";
import { Input as UiInput } from "@/components/ui/input";
import { Textarea as UiTextarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * The pieces every page uses, built on shadcn/ui (components/ui). Pages keep
 * calling these rather than the shadcn components directly, so the panel has
 * one vocabulary: a Panel is a card, a Table becomes cards on a phone, a
 * Status is a tinted pill.
 */

const BUTTON_VARIANT = {
  default: "default",
  quiet: "outline",
  danger: "destructive",
  ghost: "ghost",
} as const;

export function Button({
  variant = "default",
  size = "md",
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "default" | "quiet" | "danger" | "ghost";
  size?: "sm" | "md";
}) {
  return (
    <UiButton
      variant={BUTTON_VARIANT[variant]}
      size={size === "sm" ? "sm" : "default"}
      className={className}
      {...props}
    />
  );
}

export function Input({ className, ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  return <UiInput className={cn("bg-card", className)} {...props} />;
}

/** A native select dressed as a shadcn input: dependable on every phone. */
export function Select({ className, ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={cn(
        "h-9 max-w-full rounded-md border border-input bg-card px-3 pr-8 text-sm shadow-xs outline-none",
        "transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    />
  );
}

export function Textarea({
  className,
  ...props
}: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <UiTextarea className={cn("min-h-24 bg-card", className)} {...props} />;
}

/** An on/off switch for a row's active flag. */
export function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <Switch checked={checked} onCheckedChange={onChange} aria-label={label} disabled={disabled} />
  );
}

/**
 * The player's anonymised handle. Mono and slightly heavier than the text
 * beside it: it is the primary key of the whole system — the CS group never
 * sees a name, only this.
 */
export function PlayerNo({ value, className }: { value: number; className?: string }) {
  return (
    <span className={cn("mono text-[14px] font-semibold tracking-tight", className)}>
      {formatPlayerNo(value)}
    </span>
  );
}

/** Any machine value: chat ids, codes, update ids, file ids. */
export function Mono({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn("mono text-[13px]", className)}>{children}</span>;
}

const STATUS_TONE: Record<string, string> = {
  active: "bg-ok/10 text-ok ring-ok/20",
  sent: "bg-ok/10 text-ok ring-ok/20",
  delivered: "bg-ok/10 text-ok ring-ok/20",
  banned: "bg-destructive/10 text-destructive ring-destructive/20",
  failed: "bg-destructive/10 text-destructive ring-destructive/20",
  error: "bg-destructive/10 text-destructive ring-destructive/20",
  blocked: "bg-warn/10 text-warn ring-warn/25",
  deactivated: "bg-warn/10 text-warn ring-warn/25",
  warn: "bg-warn/10 text-warn ring-warn/25",
};

/** A status as a tinted pill: green is working, amber is look, red is broken. */
export function Status({ value, label }: { value: string; label?: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ring-1 ring-inset",
        STATUS_TONE[value] ?? "bg-muted text-muted-foreground ring-border",
      )}
    >
      <span className="size-1.5 rounded-full bg-current" aria-hidden />
      {label ?? value}
    </span>
  );
}

/** A card: optional header with a title, a line of description and an action. */
export function Panel({
  id,
  title,
  description,
  action,
  children,
  className,
}: {
  id?: string;
  title?: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      id={id}
      className={cn(
        "scroll-mt-3 overflow-hidden rounded-xl border border-border bg-card shadow-sm",
        className,
      )}
    >
      {title && (
        <header className="flex min-h-14 items-center justify-between gap-3 border-b border-border px-5 py-3">
          <div className="min-w-0">
            <h2 className="text-[15px] leading-tight font-semibold">{title}</h2>
            {description && <p className="mt-0.5 text-muted-foreground">{description}</p>}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

/**
 * A table on a tablet or desktop, a stack of cards on a phone (see `.rtable`
 * in index.css). Give every `Td` a `label` so the card can name each value,
 * and mark the one cell that identifies the row as `primary`.
 */
export function Table({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="rtable w-full border-collapse text-left text-sm [&_tbody_tr]:transition-colors [&_tbody_tr:hover]:bg-muted/50">
        {children}
      </table>
    </div>
  );
}

export function Th({ children, className }: { children?: ReactNode; className?: string }) {
  return (
    <th
      className={cn(
        "h-10 border-b border-border bg-muted/40 px-4 text-xs font-medium tracking-wide text-muted-foreground uppercase",
        className,
      )}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  className,
  label,
  primary,
}: {
  children?: ReactNode;
  className?: string;
  /** Names the value when the row is shown as a card on a phone. */
  label?: string;
  /** The cell that identifies the row: the card's heading on a phone. */
  primary?: boolean;
}) {
  return (
    <td
      data-label={label}
      data-primary={primary ? "" : undefined}
      className={cn("border-b border-border px-4 py-3 align-middle", className)}
    >
      {children}
    </td>
  );
}

/** An empty screen is an invitation to act, not a shrug. */
export function Empty({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
      <div className="mb-1 grid size-10 place-items-center rounded-full bg-muted text-muted-foreground">
        <Inbox className="size-5" />
      </div>
      <p className="font-medium">{title}</p>
      {hint && <p className="max-w-md text-muted-foreground">{hint}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  const message =
    error instanceof Error ? error.message : "Something went wrong loading this page.";
  return (
    <div
      role="alert"
      className="flex items-start gap-2.5 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-destructive"
    >
      <AlertCircle className="mt-0.5 size-4 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

export function Loading({ label = "Loading" }: { label?: string }) {
  return (
    <div className="grid gap-3 px-5 py-6" aria-busy="true" aria-label={label}>
      <Skeleton className="h-4 w-1/3" />
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-4 w-1/2" />
    </div>
  );
}

export function Field({
  label,
  hint,
  group,
  children,
}: {
  label: string;
  hint?: string;
  /**
   * For a control that is not a single input, such as the rich-text editor:
   * a label would forward every click inside it to its first button.
   */
  group?: boolean;
  children: ReactNode;
}) {
  const Tag = group ? "div" : "label";
  return (
    <Tag className="grid gap-1.5" {...(group ? { role: "group", "aria-label": label } : {})}>
      <Label asChild>
        <span>{label}</span>
      </Label>
      {children}
      {hint && <span className="text-muted-foreground">{hint}</span>}
    </Tag>
  );
}
